import { Injectable, OnDestroy } from '@angular/core';
import { CONSTANTS } from '../../../Constants/Constants';
import { IReportData } from '../../../models/reports/IReportData';
import { ReportPageOrientation } from '../../../models/reports/ReportPageOrientation';
import { ReportList } from '../../../models/dtos/Enums/ReportList';
import { TranslateService } from '@ngx-translate/core';
import { CommonDataService } from '../../common-data.service';
import { CommonFunctionsService } from '../../common-functions.service';
import { ITableData } from '../../../models/reports/ITableData';
import { IData } from '../../../models/reports/IData';
import { IndexDBService } from '../../common/indexDB.service';
import { LaborData, LaborLevelwiseData, SQWebArea, SQWebProject, SQWebSystem, Task, TypicalOfSystemHelper } from '../../../models/dtos';
import { IFooter } from '../../../models/reports/IFooter';
import { SQWebAreaChildren } from '../../../models/SQWebAreaChildren';
import { IDataItem } from '../../../models/reports/IDataItem';
import { ReportsCommonService } from '../reports-common/reports-common.service';
import { TaskBasedReportCreationOptions } from '../../../models/reports/TaskBasedReportCreationOptions';
import { DecimalPrecisionPipe } from '../../../pipes/decimal-precision-pipe/decimal-precision.pipe';

type TblAreaTableDataType = {
  header: string;
  subHeader: string;
  baseTasks: Task[];
  adjustedTasks: Task[];
  isRootLevel: boolean;
  isSystem: boolean;
  areaUniqueId: string;
  typicalOfSystemUniqueId: string;
}

type TblAreaTableGroup = {
  header: string;
  isRootLevel: boolean;
  items: TblAreaTableDataType[];
}

@Injectable()
export class TaskBasedLaborService implements OnDestroy {

  private taskBasedLaborArea: ITableData[] = [];
  private taskBasedLaborSummary: ITableData[] = [];
  private readonly taskBasedLaborSummaryMap = new Map<string, boolean>();
  private tblSummaryData: Task[] = [];
  private tblAreaDataMap = new Map<string, TblAreaTableGroup>();
  // Holds the typical-of system helpers dictionary from CommonDataService
  private typicalOfSystemsHelper: Record<string, TypicalOfSystemHelper> = {};
  private readonly TITLE_COLUMN_WIDTH: string = '150';

  constructor(private readonly commonDataService: CommonDataService,
    private readonly translate: TranslateService,
    private readonly decimalPrecisionPipe: DecimalPrecisionPipe,
    private readonly indexDbService: IndexDBService,
    private readonly commonFunctionsService: CommonFunctionsService,
    private readonly reportsCommonService: ReportsCommonService) { }

  async generateReport(reportId: ReportList, pageOrientation: ReportPageOrientation = CONSTANTS.REPORT_PAGEORIENTATION_PORTRAIT, internalReport: boolean = true) {
    const tableData = await this.generateTableData(reportId);
    let reportData: IReportData;
    const reportName = this.reportsCommonService.getSelectedReportName();
    const reportHeaderData = this.commonDataService.getReportHeaderData();
    reportData = {
      selectionName: this.translate.instant(reportName),
      reportTitle: this.translate.instant(reportName),
      reportTitles: {
        data: this.reportsCommonService.generateReportTitles(reportHeaderData)
      },
      tableData: tableData,
      pageOrientation: pageOrientation,
      internalReport: internalReport
    }
    this.reportsCommonService.setSelectedReport(reportData);
  }

  async generateTableData(reportId: ReportList): Promise<ITableData[]> {
    const reportMap: Map<ReportList, { cache: ITableData[], generator: () => Promise<void> }> = new Map([
      [ReportList.TaskBasedLaborByAreaViewModel, {
        cache: this.taskBasedLaborArea,
        generator: () => this.generateTaskBasedLaborByAreaReportData()
      }],
      [ReportList.TaskBasedLaborSummaryViewModel, {
        cache: this.taskBasedLaborSummary,
        generator: () => this.generateTaskBasedLaborSummaryReportData()
      }]
    ]);
    const report = reportMap.get(reportId);
    if (!report) {
      return [];
    }
    if (report.cache.length === 0) {
      await report.generator();
    }
    return report.cache;
  }

  async generateTaskBasedLaborByAreaReportData(): Promise<void> {
    this.tblAreaDataMap.clear();
    await this.generateTaskBasedLaborByAreaData();

    for (const group of this.tblAreaDataMap.values()) {
      const tblAreaDataSource: IData[] = [];
      const totalLevelHours: Task = new Task();

      for (const tblAreaDataType of group.items) {
        if (!(tblAreaDataType?.baseTasks?.length > 0 || tblAreaDataType?.adjustedTasks?.length > 0)) {
          continue;
        }
        const baseTaskSubTotal: Task = new Task();
        const adjustedTaskSubTotal: Task = new Task();

        // Add Sub-Area/System header
        if (tblAreaDataType.subHeader) {
          this.createTbsTableEmptyRow(tblAreaDataSource, tblAreaDataType.subHeader, CONSTANTS.REPORT_WHITE_COLOR, tblAreaDataType.isSystem);
        }

        // Process Base Tasks
        this.processBaseTasks(tblAreaDataType, tblAreaDataSource, baseTaskSubTotal, totalLevelHours);
        // Process Adjusted Tasks
        this.processAdjustedTasks(tblAreaDataType, tblAreaDataSource, adjustedTaskSubTotal, totalLevelHours);
      }

      // Add total row and create table
      if (tblAreaDataSource.length > 0) {
        totalLevelHours.description = group.isRootLevel ? 'TOTAL_PROJECT_LEVEL_HOURS' : 'TOTAL_AREA_LEVEL_HOURS';
        const footerRow = this.createTbsTableFooterRow(totalLevelHours);
        this.createTbsAreaTableData(tblAreaDataSource, group.header, footerRow, group.isRootLevel);
      }
    }
  }

  private processAdjustedTasks(tblAreaDataType: TblAreaTableDataType, tblAreaDataSource: IData[], adjustedTaskSubTotal: Task, totalAreaLevelHours: Task) {
    if (tblAreaDataType.adjustedTasks.length > 0) {
      this.createTbsTableEmptyRow(tblAreaDataSource, 'TASK_ADJUSTMENTS', CONSTANTS.REPORT_LIGHT_BLUE_COLOR);
      for (const task of this.aggregateTasksByReference(tblAreaDataType.adjustedTasks)) {
        this.createTbsTableRow(task, tblAreaDataSource);
        this.getTotalRow(adjustedTaskSubTotal, task);
      }
      this.getTotalRow(totalAreaLevelHours, adjustedTaskSubTotal);
      adjustedTaskSubTotal.description = this.translate.instant('SUB_TOTAL_ADJUSTED_HOURS');
      this.createTbsTableRow(adjustedTaskSubTotal, tblAreaDataSource);
    }
  }

  private processBaseTasks(tblAreaDataType: TblAreaTableDataType, tblAreaDataSource: IData[], baseTaskSubTotal: Task, totalAreaLevelHours: Task) {
    if (tblAreaDataType.baseTasks.length > 0) {
      this.createTbsTableEmptyRow(tblAreaDataSource, 'BASE_TASKS', CONSTANTS.REPORT_LIGHT_BLUE_COLOR);
      for (const task of this.aggregateTasksByReference(tblAreaDataType.baseTasks)) {
        this.createTbsTableRow(task, tblAreaDataSource);
        this.getTotalRow(baseTaskSubTotal, task);
      }
      this.getTotalRow(totalAreaLevelHours, baseTaskSubTotal);
      baseTaskSubTotal.description = this.translate.instant('SUB_TOTAL_BASE_HOURS');
      this.createTbsTableRow(baseTaskSubTotal, tblAreaDataSource, true);
    }
  }

  /**
   * Merges tasks sharing the same referenceNumber+description into a single row so a task that's
   * listed more than once for a system (e.g. a shared/default task) is shown only once per system.
   */
  private aggregateTasksByReference(tasks: Task[]): Task[] {
    if (!tasks?.length) {
      return tasks ?? [];
    }
    const mergedByKey = new Map<string, Task>();
    const order: string[] = [];
    for (const task of tasks) {
      const key = `${task.referenceNumber?.trim()}|${task.description?.trim()}`;
      const existing = mergedByKey.get(key);
      if (existing) {
        this.getTotalRow(existing, task);
      } else {
        mergedByKey.set(key, { ...task });
        order.push(key);
      }
    }
    return order.map(key => mergedByKey.get(key));
  }

  async generateTaskBasedLaborSummaryReportData(): Promise<void> {
    this.tblSummaryData = [];
    this.taskBasedLaborSummaryMap.clear();
    await this.generateTaskBasedLaborSummaryData();
    this.tblSummaryData.sort((left, right) => {
      const leftIsPriority = left.referenceNumber === 'T157';
      const rightIsPriority = right.referenceNumber === 'T157';
      if (leftIsPriority !== rightIsPriority) {
        return leftIsPriority ? -1 : 1;
      }
      return left.description.localeCompare(right.description);
    });
    let tblSummaryDataSource: IData[] = [];
    let totalRow: Task = new Task();
    for (const task of this.tblSummaryData) {
      this.getTotalRow(totalRow, task);
      this.createTbsTableRow(task, tblSummaryDataSource);
    }
    totalRow.description = 'TOTAL';
    this.createTbsTableData(tblSummaryDataSource, 'TASKS', totalRow);
  }

  /**
   * Generates data specifically for Task Based Labor By Area report
   */
  private async generateTaskBasedLaborByAreaData(): Promise<void> {
    const sqWebProject = this.commonDataService.getSQWebProject();
    if (sqWebProject.isItemVisible) {
      const laborLevelwiseData = await this.indexDbService.getAllLaborData();
      this.processAllLaborDataForAreaReport(laborLevelwiseData, sqWebProject);
    }
  }

  /**
   * Generates data specifically for Task Based Labor Summary report
   */
  private async generateTaskBasedLaborSummaryData(): Promise<void> {
    const sqWebProject = this.commonDataService.getSQWebProject();
    if (sqWebProject.isItemVisible) {
      const laborLevelwiseData = await this.indexDbService.getAllLaborData();
      this.processAllLaborDataForSummaryReport(laborLevelwiseData, sqWebProject);
    }
  }

  /**
   * Processes labor data for Summary Report only
   */
  private processLaborDataForSummaryReport(laborData: LaborData) {
    if (laborData.baseTasksCollection) {
      laborData.baseTasksCollection.forEach(baseTask => {
        if (this.checkExistingTask(baseTask)) {
          this.updateTaskHours(baseTask);
        } else {
          this.taskBasedLaborSummaryMap.set(baseTask.referenceNumber.trim(), true);
          this.tblSummaryData.push(baseTask);
        }
      });
    }
    if (laborData.tasksCollection) {
      laborData.tasksCollection.forEach(task => {
        if (this.checkExistingTask(task)) {
          this.updateTaskHours(task);
        } else {
          this.taskBasedLaborSummaryMap.set(task.referenceNumber.trim(), true);
          this.tblSummaryData.push(task);
        }
      });
    }
  }

  processTblAreaData(
    itemName: string,
    childName: string,
    baseTasks: Task[],
    adjustedTasks: Task[],
    options: TaskBasedReportCreationOptions
  ) {
    const tblAreaDataType: TblAreaTableDataType = {
      header: itemName,
      subHeader: childName,
      baseTasks: baseTasks,
      adjustedTasks: adjustedTasks,
      isRootLevel: options?.isRootLevel ?? false,
      isSystem: options?.isSystem ?? false,
      areaUniqueId: options?.areaUniqueId ?? '',
      typicalOfSystemUniqueId: options?.typicalOfSystemUniqueId ?? ''
    }
    const key = tblAreaDataType.isRootLevel
      ? `root:${tblAreaDataType.header}`
      : `area:${tblAreaDataType.areaUniqueId}`;
    let group = this.tblAreaDataMap.get(key);
    if (!group) {
      group = { header: tblAreaDataType.header, isRootLevel: tblAreaDataType.isRootLevel, items: [] };
      this.tblAreaDataMap.set(key, group);
    }
    group.items.push(tblAreaDataType);
  }

  /**
   * Process all labor data for Area Report
   */
  processAllLaborDataForAreaReport(laborLevelwiseData: LaborLevelwiseData, sqWebProject: SQWebProject) {
    if (!laborLevelwiseData.laborsCollection) {
      return;
    }
    const projectLaborData = laborLevelwiseData.laborsCollection[sqWebProject.itemUniqueId];
    const options: TaskBasedReportCreationOptions = { isRootLevel: true };
    if (projectLaborData) {
      this.processTblAreaData('PROJECT_LEVEL_TASKS', '', projectLaborData.baseTasksCollection, projectLaborData.tasksCollection, options);
    }
    // Process all areas recursively
    if (sqWebProject.areas) {
      for (const area of sqWebProject.areas) {
        if (area.isItemVisible) {
          this.processAreaLaborDataForAreaReport(area, laborLevelwiseData);
        }
      }
    }
  }

  /**
   * Process all labor data for Summary Report
   */
  processAllLaborDataForSummaryReport(laborLevelwiseData: LaborLevelwiseData, sqWebProject: SQWebProject) {
    if (!laborLevelwiseData.laborsCollection) {
      return;
    }
    // Process project level labor data
    this.processLaborDataForSummaryReport(laborLevelwiseData.laborsCollection[sqWebProject.itemUniqueId]);
    // Process all areas recursively
    if (sqWebProject.areas) {
      for (const area of sqWebProject.areas) {
        if (area.isItemVisible) {
          this.processAreaLaborDataForSummaryReport(area, laborLevelwiseData);
        }
      }
    }
  }

  /**
   * Process area labor data for Area Report
   */
  private processAreaLaborDataForAreaReport(area: SQWebArea, laborLevelwiseData: LaborLevelwiseData) {
    // Process current area's labor data
    const areaLaborData = laborLevelwiseData.laborsCollection[area.itemUniqueId];
    const options: TaskBasedReportCreationOptions = { areaUniqueId: area.itemUniqueId };
    this.processTblAreaData(area.itemName, '', areaLaborData.baseTasksCollection, areaLaborData.tasksCollection, options);

    // Process area children
    for (const child of area.children) {
      const childItem = child as SQWebAreaChildren;
      if (childItem.isItemVisible) {
        if (childItem.nodeType === CONSTANTS.NODETYPE_AREA) {
          // Recursively process sub-areas
          this.processAreaLaborDataForAreaReport(child as SQWebArea, laborLevelwiseData);
        } else if (childItem.nodeType === CONSTANTS.NODETYPE_BSETSYSTEM) {
          const system = childItem as SQWebSystem;
          this.processSystemLaborDataForAreaReport(system, area, laborLevelwiseData);
        }
      }
    }
  }

  /**
   * Process area labor data for Summary Report
   */
  private processAreaLaborDataForSummaryReport(area: SQWebArea, laborLevelwiseData: LaborLevelwiseData) {
    // Process current area's labor data
    this.processLaborDataForSummaryReport(laborLevelwiseData.laborsCollection[area.itemUniqueId]);
    // Process area children
    for (const child of area.children) {
      const childItem = child as SQWebAreaChildren;
      if (childItem.isItemVisible) {
        if (childItem.nodeType === CONSTANTS.NODETYPE_AREA) {
          // Recursively process sub-areas
          this.processAreaLaborDataForSummaryReport(child as SQWebArea, laborLevelwiseData);
        } else if (childItem.nodeType === CONSTANTS.NODETYPE_BSETSYSTEM) {
          const system = childItem as SQWebSystem;
          this.processSystemLaborDataForSummaryReport(system, laborLevelwiseData);
        }
      }
    }
  }

  /**
   * Process system labor data for Area Report
   */
  private processSystemLaborDataForAreaReport(system: SQWebSystem, area: SQWebArea, laborLevelwiseData: LaborLevelwiseData) {
    // Track typical-of systems
    if (system.hasTypicalOf) {
      const typicalId = system.systemTypicalOfHelperUniqueId;
      // Check if we already processed this typical group in this area
      const existingEntry = this.tblAreaDataMap.get(`area:${area.itemUniqueId}`)?.items.find(
        entry => entry.typicalOfSystemUniqueId === typicalId && entry.areaUniqueId === area.itemUniqueId
      );
      if (existingEntry) {
        return;
      }
      
      const collectionHolderId = this.commonFunctionsService.getCollectionHolderId(system);
      const typicalOfCount = this.commonFunctionsService.getSystemTypicalOfCount(system);
      const laborData = laborLevelwiseData.laborsCollection[collectionHolderId];
      const systemCountInArea = this.commonFunctionsService.getSystemTypicalOfCountInArea(system, area.itemUniqueId);

      // Clone tasks before mutating to avoid corrupting source data
      const clonedBaseTasks = laborData?.baseTasksCollection?.map(task => ({ ...task })) ?? [];
      const clonedAdjustedTasks = laborData?.tasksCollection?.map(task => ({ ...task })) ?? [];

      clonedBaseTasks.forEach(task => {
        this.multiplyTaskHours(task, systemCountInArea);
      });
      clonedAdjustedTasks.forEach(task => {
        this.multiplyTaskHours(task, systemCountInArea);
      });
      let subHeader = `Typical of ${typicalOfCount} ${system.itemName} [ ${systemCountInArea} in Area ]`;

      // Add to area data map
      this.processTblAreaData(
        area.itemName,
        subHeader,
        clonedBaseTasks,
        clonedAdjustedTasks,
        {
          isRootLevel: false,
          isSystem: true,
          areaUniqueId: area.itemUniqueId,
          typicalOfSystemUniqueId: typicalId
        }
      );
    } else {
      // Non-typical system - process normally
      const laborData = laborLevelwiseData.laborsCollection[system.itemUniqueId];
      const options: TaskBasedReportCreationOptions = { isRootLevel: false, isSystem: true, areaUniqueId: area.itemUniqueId };
      this.processTblAreaData(area.itemName, system.itemName, laborData.baseTasksCollection, laborData.tasksCollection, options);
    }
  }

  /**
   * Process system labor data for Summary Report
   */
  private processSystemLaborDataForSummaryReport(system: SQWebSystem, laborLevelwiseData: LaborLevelwiseData) {
    const laborData = laborLevelwiseData.laborsCollection[system.itemUniqueId];
    if (laborData && (laborData.baseTasksCollection?.length > 0 || laborData.tasksCollection?.length > 0)) {
      this.processLaborDataForSummaryReport(laborData);
    }
  }

  /**
   * Multiplies all hours in a task by the given count
   */
  private multiplyTaskHours(task: Task, count: number): void {
    task.projectManagementLabor = (task.projectManagementLabor || 0) * count;
    task.engineerLabor = (task.engineerLabor || 0) * count;
    task.totalCyberSecEngineerHours = (task.totalCyberSecEngineerHours || 0) * count;
    task.totalElectricalEngineerHours = (task.totalElectricalEngineerHours || 0) * count;
    task.totalLEngineerHours = (task.totalLEngineerHours || 0) * count;
    task.totalNetworkEngineerHours = (task.totalNetworkEngineerHours || 0) * count;
    task.totalQualificationEngineerHours = (task.totalQualificationEngineerHours || 0) * count;
    task.totalGraphicsHours = (task.totalGraphicsHours || 0) * count;
    task.totalProgrammingHours = (task.totalProgrammingHours || 0) * count;
    task.specialistLabor = (task.specialistLabor || 0) * count;
    task.totalDigitalDeploymentHours = (task.totalDigitalDeploymentHours || 0) * count;
    task.mechanicalEngineerLabor = (task.mechanicalEngineerLabor || 0) * count;
    task.electricalEngineerLabor = (task.electricalEngineerLabor || 0) * count;
  }

  private createTbsTableData(tblSummaryData: IData[], title: string, totalRow?: Task) {
    this.taskBasedLaborSummary.push({
      title: this.getTitleRow(title, false),
      tableFooter: this.createTbsTableFooterRow(totalRow),
      data: tblSummaryData,
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }

  private createTbsAreaTableData(tblAreaData: IData[], title: string, footer: IFooter[], isRootLevel: boolean = false) {
    this.taskBasedLaborArea.push({
      title: this.getTitleRow(title, true, isRootLevel),
      tableFooter: footer,
      data: tblAreaData,
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }

  getTitleRow(title: string, istblAreaReport: boolean, isRootLevel: boolean = false): IDataItem[] {
    let titleDescription = '';
    if (isRootLevel) {
      titleDescription = this.translate.instant('PROJECT_LEVEL_TASKS');
    } else if (istblAreaReport) {
      titleDescription = this.translate.instant('AREA_LEVEL') + ' : ' + title;
    } else {
      titleDescription = this.translate.instant(title);
    }
    return [
        { data: titleDescription, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT, width: this.TITLE_COLUMN_WIDTH },
        { data: 'PM', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { data: 'ENG', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { data: 'CENG', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { data: 'EENG', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { data: 'LENG', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { data: 'NENG', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { data: 'QENG', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { data: 'GRAP', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { data: 'PROG', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { data: 'SPEC', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { data: 'DDS', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { data: 'MECH', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { data: 'ELEC', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT }
      ];
  }

  private createTbsTableRow(task: Task, dataRows: IData[], bold: boolean = false) {
    let rowData: IData = {
      data: [
        { data: task.description, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_WHITE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT, bold },
        { data: this.validateTaskHours(task.projectManagementLabor), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_WHITE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT, bold },
        { data: this.validateTaskHours(task.engineerLabor), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_WHITE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT, bold },
        { data: this.validateTaskHours(task.totalCyberSecEngineerHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_WHITE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT, bold },
        { data: this.validateTaskHours(task.totalElectricalEngineerHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_WHITE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT, bold },
        { data: this.validateTaskHours(task.totalLEngineerHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_WHITE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT, bold },
        { data: this.validateTaskHours(task.totalNetworkEngineerHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_WHITE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT, bold },
        { data: this.validateTaskHours(task.totalQualificationEngineerHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_WHITE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT, bold },
        { data: this.validateTaskHours(task.totalGraphicsHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_WHITE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT, bold },
        { data: this.validateTaskHours(task.totalProgrammingHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_WHITE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT, bold },
        { data: this.validateTaskHours(task.specialistLabor), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_WHITE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT, bold },
        { data: this.validateTaskHours(task.totalDigitalDeploymentHours), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_WHITE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT, bold },
        { data: this.validateTaskHours(task.mechanicalEngineerLabor), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_WHITE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT, bold },
        { data: this.validateTaskHours(task.electricalEngineerLabor), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_WHITE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT, bold }
      ]
    };
    dataRows.push(rowData);
  }

  private createTbsTableFooterRow(task: Task): IFooter[] {
    let footerData: IFooter = {
      headers: [
        { headerText: this.translate.instant(task.description), columnspan: CONSTANTS.ONE },
        { headerText: this.validateTaskHours(task.projectManagementLabor), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { headerText: this.validateTaskHours(task.engineerLabor), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { headerText: this.validateTaskHours(task.totalCyberSecEngineerHours), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { headerText: this.validateTaskHours(task.totalElectricalEngineerHours), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { headerText: this.validateTaskHours(task.totalLEngineerHours), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { headerText: this.validateTaskHours(task.totalNetworkEngineerHours), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { headerText: this.validateTaskHours(task.totalQualificationEngineerHours), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { headerText: this.validateTaskHours(task.totalGraphicsHours), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { headerText: this.validateTaskHours(task.totalProgrammingHours), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { headerText: this.validateTaskHours(task.specialistLabor), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { headerText: this.validateTaskHours(task.totalDigitalDeploymentHours), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { headerText: this.validateTaskHours(task.mechanicalEngineerLabor), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { headerText: this.validateTaskHours(task.electricalEngineerLabor), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.CENTER_ALIGNMENT }
      ]
    }
    return [footerData];
  }

  private createTbsTableEmptyRow(dataRows: IData[], title: string, color: string = CONSTANTS.REPORT_WHITE_COLOR, isSystemRow: boolean = false) {
    if (isSystemRow) {
      dataRows.push({
        data: [
          { data: this.translate.instant(title), columnspan: 14, hierarchyLevel: CONSTANTS.ONE, DataCellColor: color, alignment: CONSTANTS.LEFT_ALIGNMENT }
        ]
      });
      return;
    }

    let rowData: IData = {
      data: [
        { data: this.translate.instant(title), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: color, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: '', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: color },
        { data: '', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: color },
        { data: '', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: color },
        { data: '', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: color },
        { data: '', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: color },
        { data: '', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: color },
        { data: '', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: color },
        { data: '', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: color },
        { data: '', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: color },
        { data: '', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: color },
        { data: '', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: color },
        { data: '', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: color },
        { data: '', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: color },
        { data: '', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: color }
      ]

    }
    dataRows.push(rowData);
  }

  private validateTaskHours(hours: number): string {
    if (hours === undefined || hours === null || hours === CONSTANTS.NEGATIVE_ONE) {
      return String(CONSTANTS.ZERO);
    }
    return this.decimalPrecisionPipe.transform(hours);
  }

  private checkExistingTask(task: Task): boolean {
    return this.taskBasedLaborSummaryMap.has(task.referenceNumber.trim());
  }

  private updateTaskHours(task: Task): void {
    this.tblSummaryData.forEach((row: Task) => {
      if (task.referenceNumber.trim() === row.referenceNumber.trim() &&
        task.description.trim() === row.description.trim()) {
        row.projectManagementLabor += task.projectManagementLabor === CONSTANTS.ZERO
          ? CONSTANTS.ZERO
          : task.projectManagementLabor;
        row.engineerLabor += task.engineerLabor === CONSTANTS.NEGATIVE_ONE
          ? CONSTANTS.ZERO
          : task.engineerLabor;
        row.totalCyberSecEngineerHours += task.totalCyberSecEngineerHours === CONSTANTS.NEGATIVE_ONE
          ? CONSTANTS.ZERO
          : task.totalCyberSecEngineerHours;
        row.totalElectricalEngineerHours += task.totalElectricalEngineerHours === CONSTANTS.NEGATIVE_ONE
          ? CONSTANTS.ZERO
          : task.totalElectricalEngineerHours;
        row.totalLEngineerHours += task.totalLEngineerHours === CONSTANTS.NEGATIVE_ONE
          ? CONSTANTS.ZERO
          : task.totalLEngineerHours;
        row.totalNetworkEngineerHours += task.totalNetworkEngineerHours === CONSTANTS.NEGATIVE_ONE
          ? CONSTANTS.ZERO
          : task.totalNetworkEngineerHours;
        row.totalQualificationEngineerHours += task.totalQualificationEngineerHours === CONSTANTS.NEGATIVE_ONE
          ? CONSTANTS.ZERO
          : task.totalQualificationEngineerHours;
        row.totalGraphicsHours += task.totalGraphicsHours === CONSTANTS.NEGATIVE_ONE
          ? CONSTANTS.ZERO
          : task.totalGraphicsHours;
        row.totalProgrammingHours += task.totalProgrammingHours === CONSTANTS.NEGATIVE_ONE
          ? CONSTANTS.ZERO
          : task.totalProgrammingHours;
        row.specialistLabor += task.specialistLabor === CONSTANTS.NEGATIVE_ONE
          ? CONSTANTS.ZERO
          : task.specialistLabor;
        row.totalDigitalDeploymentHours += task.totalDigitalDeploymentHours === CONSTANTS.NEGATIVE_ONE
          ? CONSTANTS.ZERO
          : task.totalDigitalDeploymentHours;
        row.mechanicalEngineerLabor += task.mechanicalEngineerLabor === CONSTANTS.NEGATIVE_ONE
          ? CONSTANTS.ZERO
          : task.mechanicalEngineerLabor;
        row.electricalEngineerLabor += task.electricalEngineerLabor === CONSTANTS.NEGATIVE_ONE
          ? CONSTANTS.ZERO
          : task.electricalEngineerLabor;
      }
    });
  }

  /** Sums a labor hours field, treating the -1 "not applicable" sentinel as 0 (matches validateTaskHours). */
  private addLaborHours(current: number, value: number): number {
    const safeCurrent = current ?? 0;
    if (value === undefined || value === null || value === CONSTANTS.NEGATIVE_ONE) {
      return safeCurrent;
    }
    return safeCurrent + value;
  }

  getTotalRow(totalRow: Task, task: Task) {
    totalRow.projectManagementLabor = this.addLaborHours(totalRow.projectManagementLabor, task.projectManagementLabor);
    totalRow.engineerLabor = this.addLaborHours(totalRow.engineerLabor, task.engineerLabor);
    totalRow.totalCyberSecEngineerHours = this.addLaborHours(totalRow.totalCyberSecEngineerHours, task.totalCyberSecEngineerHours);
    totalRow.totalElectricalEngineerHours = this.addLaborHours(totalRow.totalElectricalEngineerHours, task.totalElectricalEngineerHours);
    totalRow.totalLEngineerHours = this.addLaborHours(totalRow.totalLEngineerHours, task.totalLEngineerHours);
    totalRow.totalNetworkEngineerHours = this.addLaborHours(totalRow.totalNetworkEngineerHours, task.totalNetworkEngineerHours);
    totalRow.totalQualificationEngineerHours = this.addLaborHours(totalRow.totalQualificationEngineerHours, task.totalQualificationEngineerHours);
    totalRow.totalGraphicsHours = this.addLaborHours(totalRow.totalGraphicsHours, task.totalGraphicsHours);
    totalRow.totalProgrammingHours = this.addLaborHours(totalRow.totalProgrammingHours, task.totalProgrammingHours);
    totalRow.specialistLabor = this.addLaborHours(totalRow.specialistLabor, task.specialistLabor);
    totalRow.totalDigitalDeploymentHours = this.addLaborHours(totalRow.totalDigitalDeploymentHours, task.totalDigitalDeploymentHours);
    totalRow.mechanicalEngineerLabor = this.addLaborHours(totalRow.mechanicalEngineerLabor, task.mechanicalEngineerLabor);
    totalRow.electricalEngineerLabor = this.addLaborHours(totalRow.electricalEngineerLabor, task.electricalEngineerLabor);
  }

  ngOnDestroy() {
    this.tblAreaDataMap.clear();
    this.taskBasedLaborArea = [];
    this.typicalOfSystemsHelper = {};
    this.tblSummaryData = [];
    this.taskBasedLaborSummaryMap.clear();
    this.taskBasedLaborSummary = [];
  }
}
